import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMap } from '../map/MapContext';
import { removeSpatialAnalysisLayer, showSpatialAnalysisLayer } from '../map/SpatialAnalysisLayer';
import { analyzeSpatialFile, type SpatialAnalysisResult } from '../services/SpatialAnalysisService';

function exportFileName(fileName: string): string {
  return `${fileName.replace(/\.[^.]+$/, '') || 'spatial-analysis'}.geojson`;
}

function formatBounds(bounds: SpatialAnalysisResult['bounds']): string {
  if (!bounds) return 'No coordinate extent';
  return bounds.map((coordinate) => coordinate.toFixed(5)).join(', ');
}

export const SpatialAnalysisPanel: React.FC = () => {
  const { map } = useMap();
  const inputRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<SpatialAnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);

  const clearPreview = useCallback(() => {
    if (map) removeSpatialAnalysisLayer(map);
    setPreviewVisible(false);
  }, [map]);

  useEffect(() => () => {
    if (map) removeSpatialAnalysisLayer(map);
  }, [map]);

  const onFile = useCallback(async (file: File | undefined) => {
    if (!file) return;
    setLoading(true);
    setError(null);
    clearPreview();
    try {
      setResult(await analyzeSpatialFile(file));
    } catch (reason) {
      setResult(null);
      setError(reason instanceof Error ? reason.message : 'Spatial analysis failed.');
    } finally {
      setLoading(false);
    }
  }, [clearPreview]);

  const togglePreview = useCallback(() => {
    if (!map || !result) return;
    if (previewVisible) {
      clearPreview();
      return;
    }
    showSpatialAnalysisLayer(map, result.geojson, result.bounds);
    setPreviewVisible(true);
  }, [clearPreview, map, previewVisible, result]);

  const exportGeoJson = useCallback(() => {
    if (!result) return;
    const url = URL.createObjectURL(new Blob([JSON.stringify(result.geojson, null, 2)], { type: 'application/geo+json' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = exportFileName(result.fileName);
    anchor.click();
    URL.revokeObjectURL(url);
  }, [result]);

  return (
    <div>
      <div
        className="panel-section"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          void onFile(event.dataTransfer.files?.[0]);
        }}
        style={{ border: '1px dashed var(--border)', borderRadius: 6, padding: 14, textAlign: 'center' }}
      >
        <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 8 }}>
          Drop GeoJSON, KML, GPX, CSV, or TSV
        </div>
        <button className="btn btn-primary" disabled={loading} onClick={() => inputRef.current?.click()}>
          {loading ? 'Analyzing...' : 'Choose file'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".geojson,.json,.kml,.gpx,.csv,.tsv,application/geo+json,application/vnd.google-earth.kml+xml,text/csv"
          style={{ display: 'none' }}
          onChange={(event) => {
            void onFile(event.target.files?.[0]);
            event.currentTarget.value = '';
          }}
        />
      </div>

      {error && <div className="panel-error">{error}</div>}

      {result && (
        <>
          <div className="panel-section">
            <div className="panel-section-title">Analysis</div>
            <div style={{ overflowWrap: 'anywhere', fontFamily: 'var(--font-mono)', fontSize: 11, marginBottom: 8 }}>
              {result.fileName}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
              <div><span style={{ color: 'var(--text-muted)' }}>Features</span><br />{result.featureCount.toLocaleString()}</div>
              <div><span style={{ color: 'var(--text-muted)' }}>Vertices</span><br />{result.vertexCount.toLocaleString()}</div>
              <div><span style={{ color: 'var(--text-muted)' }}>Valid</span><br />{result.validCount.toLocaleString()}</div>
              <div><span style={{ color: 'var(--text-muted)' }}>Invalid</span><br />{result.invalidCount.toLocaleString()}</div>
            </div>
          </div>

          <div className="panel-section">
            <div className="panel-section-title">Geometry Breakdown</div>
            {result.geometryCounts.map(({ type, count }) => (
              <div key={type} className="data-row">
                <span className="data-label">{type}</span>
                <span className="data-value">{count.toLocaleString()}</span>
              </div>
            ))}
            {result.geometryCounts.length === 0 && (
              <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>No geometries found.</div>
            )}
          </div>

          <div className="panel-section">
            <div className="panel-section-title">Bounds</div>
            <div style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', fontSize: 11, overflowWrap: 'anywhere' }}>
              {formatBounds(result.bounds)}
            </div>
          </div>

          <div className="panel-section" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" disabled={!map || result.featureCount === 0} onClick={togglePreview}>
              {previewVisible ? 'Clear preview' : 'Preview on map'}
            </button>
            <button className="btn btn-secondary" onClick={exportGeoJson}>Export GeoJSON</button>
          </div>

          <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
            Engine: {result.engine === 'duckdb-spatial' ? 'DuckDB Spatial WASM' : 'Browser fallback'}
          </div>
          {result.truncated && (
            <div className="status-yellow" style={{ fontSize: 11, marginTop: 6 }}>
              Preview and export are limited to the first 10,000 features.
            </div>
          )}
          {result.warning && (
            <div className="status-yellow" style={{ fontSize: 11, marginTop: 6 }}>{result.warning}</div>
          )}
        </>
      )}
    </div>
  );
};
